import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TypeAccess } from 'src/models/interfaces/input-interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ValidatorService } from 'src/app/services/validators/errors-validators.service';
import { Address } from 'src/models/interfaces/address/address-interface';
import { AddressService } from 'src/app/services/register-data/addresses/address.service';
import { CommonService } from 'src/app/services/common/common.service';
import { Subscription } from 'rxjs';
import { MessageService, ErrorData } from 'src/app/services/message/message.service';
import { CepResponseModel } from 'src/models/interfaces/address/cep-response-model';
import { HomeComponent } from 'src/app/home/home/home.component';
import { menuOptions } from 'src/data/menu-options.data';

@Component({
    selector: 'spe-address-details',
    templateUrl: './address-details.component.html',
    styleUrls: ['./address-details.component.scss'],
    standalone: false
})
export class AddressDetailsComponent implements OnInit, OnDestroy {
  public listAddressType: TypeAccess[] = [];
  public submitted = false;
  public address = {} as Address;
  public nav = this.routerService.getCurrentNavigation();
  public newZipCode = '';
  public idRef = 0;
  public apiStatus: string | null = null;
  public message: string = '';
  public isCepValid: boolean = true;
  private subscriptions: Subscription[] = [];
  public cepResponse?: CepResponseModel;
  public sigla = localStorage.getItem('sigla');

  public addressDataForm = new FormGroup({
    id: new FormControl(),
    addressType: new FormControl('', [Validators.required]),
    zipCode: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(8)], null),
    complement: new FormControl(''),
    reference: new FormControl(''),
    number: new FormControl('', [Validators.required], null),
    responsible: new FormControl('', [Validators.required]),
    street: new FormControl('', [Validators.required]),
    district: new FormControl('', [Validators.required]),
    city: new FormControl({ value: '', disabled: true }, [Validators.required]),
    state: new FormControl({ value: '', disabled: true }, [Validators.required])
  });

  validatorErrors = this.validatorService.validatorErrors;

  constructor(
    public readonly routerService: Router,
    public readonly addressService: AddressService,
    public readonly snackBar: MatSnackBar,
    public readonly validatorService: ValidatorService,
    public readonly commonService: CommonService,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    private homeComponent: HomeComponent
  ) {}

  ngOnInit(): void {
    this.homeComponent.setNewVariables(
      'Endereços',
      'Mantenha os endereços da empresa sempre atualizados para facilitar a localização e/ou recebimento dos benefícios.'
    );
    this.homeComponent.idleMenuContent = menuOptions[0].content;
    this.homeComponent.iconPath = 'assets/images/64_icon_map.png';
    const messageSubscription = this.messageService.errorData$.subscribe((errorData: ErrorData) => {
      this.showMessage(errorData.errorMessage, errorData.isError);
    });
    this.subscriptions.push(messageSubscription);

    this.idRef = this.activatedRoute.snapshot.params['id'];
    const subscription = this.addressService.getAddresses().subscribe({
      next: (address) => {
        if (address) {
          const enderecos: Address[] = address.enderecos;
          const thisAddress = enderecos.find((address) => address.id == this.idRef);
          const numericCep = thisAddress!.numeroCep.replace('-', '');

          this.addressDataForm.patchValue({
            id: thisAddress!.id,
            street: thisAddress!.nomeCep ?? thisAddress!.logradouro,
            district: thisAddress!.bairroCep,
            city: thisAddress!.cidade,
            state: thisAddress!.estado,
            zipCode: numericCep,
            number: thisAddress!.numero,
            addressType: thisAddress!.descricaoTipo,
            responsible: thisAddress!.responsavel,
            reference: thisAddress!.pontoReferencia,
            complement: thisAddress!.complemento
          });

          if (thisAddress?.idTipo === 1) {
            this.listAddressType = [{ value: 'Faturamento e/ou Entrega', viewValue: 'Faturamento e/ou Entrega' }];
          } else {
            this.listAddressType = [
              { value: 'Faturamento e/ou Entrega', viewValue: 'Faturamento e/ou Entrega' },
              { value: 'Entrega', viewValue: 'Entrega' }
            ];
          }

          let zipCode = `${this.addressDataFormControl.zipCode.value!.substring(
            0,
            5
          )}-${this.addressDataFormControl.zipCode.value!.substring(5, 8)}`;

          const subscription = this.addressService.getCep(zipCode).subscribe({
            next: (data) => {
              this.cepResponse = data;
              if (data.nomeLogradouro != null) {
                this.addressDataFormControl.street.disable();
              } else {
                this.addressDataFormControl.street.enable();
                this.addressDataFormControl.street.markAsUntouched();
              }
              if (data.nomeBairro != null) {
                this.addressDataFormControl.district.disable();
              } else {
                this.addressDataFormControl.district.enable();
                this.addressDataFormControl.district.markAsUntouched();
              }
            },
            error: (_) => {
              this.showMessage('CEP não encontrado.');
              this.isCepValid = false;
              this.addressDataForm.patchValue({
                street: '',
                district: '',
                city: '',
                state: ''
              });
            }
          });
          this.subscriptions.push(subscription);
        }
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public readonly findMethod = () => {
    if (this.addressDataFormControl.zipCode.status == 'INVALID') {
      this.showMessage('Digite o CEP corretamente.');
    }
    if (this.addressDataFormControl.zipCode.status == 'VALID') {
      this.newZipCode = `${this.addressDataFormControl.zipCode.value!.substring(
        0,
        5
      )}-${this.addressDataFormControl.zipCode.value!.substring(5, 8)}`;
      const subscription = this.addressService.getCep(this.newZipCode).subscribe({
        next: (data) => {
          this.cepResponse = data;
          this.apiStatus = null;
          this.isCepValid = true;
          this.addressDataForm.patchValue({
            street: data.nomeLogradouro,
            district: data.nomeBairro,
            city: data.nomeCidade,
            state: data.codigoEstado
          });
          if (data.nomeLogradouro != null) {
            this.addressDataFormControl.street.disable();
          } else {
            this.addressDataFormControl.street.enable();
            this.addressDataFormControl.street.markAsUntouched();
          }
          if (data.nomeBairro != null) {
            this.addressDataFormControl.district.disable();
          } else {
            this.addressDataFormControl.district.enable();
            this.addressDataFormControl.district.markAsUntouched();
          }
        },
        error: (_) => {
          this.showMessage('CEP não encontrado.');
          this.isCepValid = false;
          this.addressDataForm.patchValue({
            street: '',
            district: '',
            city: '',
            state: ''
          });
        }
      });
      this.subscriptions.push(subscription);
    }
  };

  public readonly saveInfo = () => {
    this.submitted = true;
    this.addressDataForm.markAllAsTouched();
    if (!this.addressDataForm.dirty) {
      this.showMessage('Nenhum campo alterado!');
    } else if (this.addressDataForm.valid && this.isCepValid) {
      this.addressDataFormControl.state.enable();
      this.addressDataFormControl.street.enable();
      this.addressDataFormControl.district.enable();
      this.addressDataFormControl.city.enable();

      let cep =
        this.addressDataFormControl.zipCode.value!.slice(0, 5) +
        '-' +
        this.addressDataFormControl.zipCode.value!.slice(5);

      let logradouro: string | null = null;
      let bairro: string | null = null;

      if (this.cepResponse?.nomeLogradouro === null) {
        logradouro = this.addressDataFormControl.street.value;
      }

      if (this.cepResponse?.nomeBairro === null) {
        bairro = this.addressDataFormControl.district.value;
      }

      const newAddress = {
        tipo: this.addressDataFormControl.addressType.value == 'Entrega' ? '3' : '1',
        cep: cep,
        numero: this.addressDataFormControl.number.value,
        complemento: this.addressDataFormControl.complement.value,
        responsavel: this.addressDataFormControl.responsible.value,
        referencia: this.addressDataFormControl.reference.value,
        id: this.idRef,
        logradouro: logradouro,
        bairro: bairro
      };

      const subscription = this.addressService.updateAddress(newAddress).subscribe({});
      this.subscriptions.push(subscription);
    }

    this.commonService.autoScroll();
  };

  public readonly returnButton = () => {
    this.routerService.navigate(['home/dadosEmpresa/endereco']);
  };

  get addressDataFormControl() {
    return this.addressDataForm.controls;
  }

  private readonly showMessage = (text: string = '', errorMessage: boolean = false) => {
    this.apiStatus = errorMessage ? 'error' : 'success';
    this.message = text;
  };
}
